import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo
} from 'react';
import { useLazyQuery } from '@apollo/client';
import { PropTypes } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useAppSlugContext } from './AppSlugContext';
import groupQueries from '../services/queries/Group';
import { UserSettingsTypes, hideGroupInsights } from '../helpers/user';
import LoadingSpinner from '../components/LoadingSpinner';
import { INSIGHTS_VISIBILITY } from '../constants/insights';
import { SUBSCRIPTION_TYPES } from '../helpers/user';
import {
  ADMIN_PERMISSION,
  CLINICIAN_ADMIN_PERMISSION,
  CLINICIAN_PERMISSION,
  CONTENT_CREATOR_PERMISSION
} from '../constants/user';
import { useWhiteLabels } from '../hooks/whiteLabels';
import { useAuthContext } from './AuthContext';

const UserDataContext = createContext({});

function useUserData() {
  return useContext(UserDataContext);
}

function UserDataProvider({ children }) {
  const { currentAppSlug, setCurrentAppSlug } = useAppSlugContext();
  const {
    isAuthenticated,
    isLoading: authContextLoading,
    user
  } = useAuthContext();
  const { i18n } = useTranslation();

  const [userData, setUserData] = useState();
  const [isInsightsAvailable, setIsInsightsAvailable] = useState(false);

  const { getUserWhiteLabel, loadingGetUserWhiteLabel } = useWhiteLabels();

  const [
    getUserDetails,
    { loading: userDetailsLoading, data: userDetailsData }
  ] = useLazyQuery(groupQueries.GET_USER_DETAILS, {
    notifyOnNetworkStatusChange: true,
    onError: (error) => {
      console.log('Error on getting User Data', error);
    }
  });

  const isLoading = useMemo(
    () => authContextLoading || userDetailsLoading || loadingGetUserWhiteLabel,
    [authContextLoading, userDetailsLoading, loadingGetUserWhiteLabel]
  );

  const isPlus = useMemo(
    () => (userData ? userData.subscriptionType !== 'Basic' : false),
    [userData]
  );

  const type = useMemo(
    () => (userData ? SUBSCRIPTION_TYPES[userData.subscriptionType] : 'Free'),
    [userData]
  );

  useEffect(() => {
    const dataUser = { ...userDetailsData?.user };

    if (
      JSON.stringify(dataUser) !== JSON.stringify(userData) &&
      dataUser &&
      user
    ) {
      const userRoles = dataUser?.roles || [];
      const isAdmin = userRoles.includes(ADMIN_PERMISSION);
      const insightsVisibility = dataUser?.groupInsightsFilter;
      if (insightsVisibility) {
        if (insightsVisibility === INSIGHTS_VISIBILITY.VISIBLE_TO_ALL) {
          setIsInsightsAvailable(true);
        } else if (insightsVisibility === INSIGHTS_VISIBILITY.NOT_VISIBLE) {
          setIsInsightsAvailable(false);
        } else if (insightsVisibility === INSIGHTS_VISIBILITY.ADMINS_ONLY) {
          setIsInsightsAvailable(isAdmin);
        }
      } else {
        setIsInsightsAvailable(true);
      }
      const isContentCreator = userRoles.includes(CONTENT_CREATOR_PERMISSION);
      const isClinician = userRoles.includes(CLINICIAN_PERMISSION);
      const isClinicianAdmin = userRoles.includes(CLINICIAN_ADMIN_PERMISSION);
      const insightsFilter = dataUser.groupInsightsFilter;

      dataUser.hideGroupInsightsMenu = hideGroupInsights(
        insightsFilter,
        isAdmin
      );

      // User definitions
      dataUser.avatar = user?.picture;
      dataUser.nickname = user.nickname;
      dataUser.sub = user.sub;
      dataUser.geoip = user['https://web.checkingin.co/geoip'];
      dataUser.challengeStartDate =
        user['https://web.checkingin.co/challengeStartDate'];
      dataUser.mailchimpId = user['https://web.checkingin.co/mailchimpId'];
      dataUser.tags = user['https://web.checkingin.co/tags'];

      dataUser.timeZone = user?.geoip?.time_zone;
      dataUser.email_verified = user.email_verified;
      dataUser.emailFull = user.email;
      dataUser.updated_at = user.updated_at;
      dataUser.name = dataUser.name || user.name;

      // DataUser definitions
      dataUser.group = dataUser.groupName;
      dataUser.isEnterprise = !!dataUser?.groupId;
      dataUser.isCompanyAdmin = isAdmin;
      dataUser.isContentCreator = isContentCreator;
      dataUser.isClinician = isClinician || isClinicianAdmin;
      dataUser.isClinicianAdmin = isClinicianAdmin;

      // UserDetailsData definitions
      dataUser.userSettings = userDetailsData?.userSetting;

      setUserData({ ...dataUser });
    }
  }, [userDetailsData, user]);

  useEffect(() => {
    if (userDetailsData?.userSetting) {
      const userLanguage = userDetailsData?.userSetting
        .find(
          (setting) =>
            setting.type === UserSettingsTypes.PREFERENCES &&
            setting.platform === 'web'
        )
        ?.meta.find((item) => item.name === 'language')?.value;

      if (!userLanguage) {
        i18n.changeLanguage('en');
      }

      if (userLanguage !== i18n.language) {
        i18n.changeLanguage(userLanguage);
      }
    }
  }, [userDetailsData?.userSetting]);

  useEffect(() => {
    async function handleGetData() {
      const whiteLabelData = await getUserWhiteLabel();

      if (
        whiteLabelData.data?.userWhiteLabel?.slug &&
        currentAppSlug !== whiteLabelData.data?.userWhiteLabel?.slug
      ) {
        setCurrentAppSlug(whiteLabelData.data?.userWhiteLabel?.slug);
      } else {
        await getUserDetails();
      }
    }

    if (user && !isLoading && isAuthenticated) handleGetData();
  }, [user, isLoading, isAuthenticated, currentAppSlug]);

  return (
    <UserDataContext.Provider
      value={{
        userData,
        isPlus,
        type,
        isInsightsAvailable,
        refetch: getUserDetails
      }}
    >
      {isLoading || Object.keys(userData || {}).length === 0 ? (
        <LoadingSpinner wrapper />
      ) : (
        children
      )}
    </UserDataContext.Provider>
  );
}

UserDataProvider.propTypes = {
  children: PropTypes.any.isRequired
};

export { UserDataProvider, useUserData };
